// 3rd party imports
import * as React from 'react';
import { inject, observer } from 'mobx-react';

// bernie
import { AnalyticsStore, ContextStore } from 'bernie-core';
import { ILocalization, LocalizedText, withLocalization } from '@shared-ui/localization-context';

// mobx
import { Quicklink } from 'models';

// ui
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkHeading, UitkText } from 'uitk-react-text';
import { UitkCard, UitkCardContentSection } from 'uitk-react-cards';
import { UitkTextSkeleton } from 'uitk-react-skeleton';

// components
import { CustomQuicklink } from './custom-quicklink';
import { ConfiguredQuicklink } from './configured-quicklink';

// models
import { Program, Product } from 'models';
import { LinkAnalyticsMicroMessageContent } from 'models/analytics';

// stores
import { UserAuthStore } from 'stores';

//util
import {
  getProgramAnalyticsMicroMessage,
  getProductAnalyticsMicroMessage,
  getLinkAnalyticsMicroMessage,
} from '../../util/analytics';
import { getPartnerizeTrackingLink } from '../../util/tracking';

interface QuicklinksProps {
  analytics?: AnalyticsStore;
  context?: ContextStore;
  quicklinks?: Quicklink[];
  userAuth?: UserAuthStore;
  program?: Program;
  product?: Product;
  l10n?: ILocalization;
  activeProgram: any;
}

interface QuicklinksState {
  loading: boolean;
}

@withLocalization
@inject('analytics', 'context', 'userAuth')
@observer
class Quicklinks extends React.Component<QuicklinksProps, QuicklinksState> {
  private camref;
  private creativeref;

  constructor(props) {
    super(props);

    this.camref = null;
    this.creativeref = null;

    this.state = {
      loading: true,
    };
  }

  private generateQuickLink = (destination) => {
    const { product, program, activeProgram } = this.props;

    const { networkTrackingBaseUrl: baseUrl } = program;
    const { programReference: camRef } = activeProgram;
    const { creativeRef } = product;

    this.camref = camRef;

    return getPartnerizeTrackingLink({ baseUrl, camRef, creativeRef, destination }) ?? destination;
  };

  private analyticsTrackEvents = (rfrr: string, link?: LinkAnalyticsMicroMessageContent) => {
    const { analytics, program, product, activeProgram } = this.props;

    if (typeof analytics?.trackEvent === 'function') {
      const microMessages = [
        getProgramAnalyticsMicroMessage(program, activeProgram.programStatus),
        getProductAnalyticsMicroMessage(product),
      ];
      const microMessagesWithLink = [...microMessages, getLinkAnalyticsMicroMessage(link)];

      const filterAnalyticsTrackEvent = {
        'affhub.product_config': () => {
          analytics.trackEvent(rfrr, 'AffHub Product Config Presented', null, null, true, microMessages);
        },
        'affhub_copy_code.selected': () => {
          analytics.trackEvent(rfrr, 'AffHub Copy Code Selected', null, null, true, microMessagesWithLink);
        },
        'affhub_copy_link.selected': () => {
          analytics.trackEvent(rfrr, 'AffHub Copy Link Selected', null, null, true, microMessagesWithLink);
        },
      };

      filterAnalyticsTrackEvent[rfrr]();
    }
  };

  public componentDidMount() {
    setTimeout(() => {
      this.setState({
        loading: false,
      });

      // TODO: Logging tracking attributes after a time delay as the values are null
      this.analyticsTrackEvents('affhub.product_config');
    }, 3500);
  }

  public render() {
    const { quicklinks, program, product, analytics, activeProgram } = this.props;
    const { loading } = this.state;

    const ConfiguredQuicklinks = ({ items, camRef }) => {
      const quicklinksList =
        items &&
        items.map((q, i) => {
          // Dynamic link param will be here
          const newUrl = this.generateQuickLink(q.link);
          const newCode = `<a href="${newUrl}">${q.text}</a>`;

          return (
            <ConfiguredQuicklink
              index={i + 1}
              name={q.name}
              title={q.title}
              code={newCode}
              pzLink={newUrl}
              link={q.link}
              key={`ql-${q.name}`}
              analyticsTrackEvents={this.analyticsTrackEvents}
            />
          );
        });

      const quicklinksSkeleton =
        items &&
        items.map((q, i) => {
          return (
            <UitkSpacing margin={{ blockend: 'four' }} key={`skeleton-${i}`}>
              <UitkCard padded={true}>
                <UitkCardContentSection>
                  <UitkTextSkeleton />
                </UitkCardContentSection>
                <UitkCardContentSection>
                  <UitkTextSkeleton animation={true} type="secondary" />
                </UitkCardContentSection>
              </UitkCard>
            </UitkSpacing>
          );
        });

      return <>{loading ? quicklinksSkeleton : quicklinksList}</>;
    };

    return (
      <main className="container">
        <CustomQuicklink
          analytics={analytics}
          activeProgram={activeProgram}
          program={program}
          product={product}
          quicklinks={quicklinks}
        />

        <UitkSpacing margin={{ blockend: 'one' }}>
          <UitkHeading tag="h2" size={4}>
            <LocalizedText message="product.quicklink.configured.title.get_pre_configured_link" />
          </UitkHeading>
        </UitkSpacing>

        <UitkSpacing margin={{ blockend: 'six' }}>
          <UitkText>
            <LocalizedText
              message="product.quicklink.configured.content.copy_link_for_popular_pages"
              data={[this.props.program.title]}
            />
          </UitkText>
        </UitkSpacing>

        <ConfiguredQuicklinks items={quicklinks} camRef={this.camref} />
      </main>
    );
  }
}

export { Quicklinks };
